/**
 * IOT-HAL-BOOTSTRAP-HARDENING-1: HalService bootstrap — hardened.
 *
 * Called once at app startup (main.jsx) after DataService is ready.
 * Replaces the `try/catch (e) { return null }` pattern (IOT-HAL-BOOTSTRAP-CONTRACT-1)
 * with explicit conformance checking via `assertAdapterContract`.
 *
 * Failure modes handled deterministically:
 *   1. Missing config    — factory returns null/undefined → conformance check fires, logs, continues
 *   2. Network down      — probe() returns { ok: false } → recorded in probeResults, not fatal
 *   3. Malformed manifest — factory throws → caught, vendor skipped, error logged
 *   4. Version mismatch  — factory throws version error → caught, vendor skipped
 *   5. Dep missing       — import error → pre-flight conformance check fires before registration
 *
 * A single broken adapter NEVER blank-screens the app. Adapters that fail
 * conformance are replaced with a NotImplementedAdapter so routing still works.
 */

import HalService from './HalService.js';
import DataService from '../DataService.js';
import { VENDOR_ADAPTER_FACTORIES } from './adapters/registry.generated.js';
import { assertAdapterContract } from './VendorAdapter.js';
import { createNotImplementedAdapter } from './NotImplementedAdapter.js';

/**
 * Per-vendor construction args for factories that require runtime values.
 * Vendors absent from this map are called with no arguments.
 */
const FACTORY_CONSTRUCTION_ARGS = {
  syntra: [null],  // cloudClient — replaced when IOT-CLOUD-1 wires AWS IoT
};

/**
 * Bootstrap HAL service.
 *
 * Supports two adapter factory patterns:
 *
 * Pattern A — Singleton vendor adapter (returns an object):
 *   createXAdapterFactory(config) → { probe(), register(), send(), ... }
 *   Bootstrap validates the HAL contract and probes before registering.
 *
 * Pattern B — Per-device factory (returns a function):
 *   createXAdapterFactory(config) → (device) => new XAdapter(device, config)
 *   HalService calls the function with a DeviceRecord at lookup time.
 *   Bootstrap registers the function directly; contract is enforced lazily.
 *
 * HAL-ADAPTER-AUDIT-FIX-1: Pattern B is the current export shape for all
 * WIRED and STUB adapters. The bootstrap now detects and handles it correctly
 * instead of misclassifying per-device factories as contract violations.
 *
 * @returns {{ registered: string[], skipped: Array<{vendorId: string, reason: string}>, probeResults: object[] }}
 */
export async function bootstrapHal() {
  HalService.injectDataService(DataService.hal);

  const registered = [];
  const skipped = [];
  const probeResults = [];

  for (const [vendorId, factory] of Object.entries(VENDOR_ADAPTER_FACTORIES)) {
    let adapter;

    // ── Step 1: Construct adapter ───────────────────────────────────────────
    try {
      const args = FACTORY_CONSTRUCTION_ARGS[vendorId] || [];
      adapter = factory(...args);
    } catch (err) {
      const reason = `factory threw during construction: ${err?.message ?? String(err)}`;
      console.warn(`[halBootstrap] "${vendorId}" skipped — ${reason}`);
      skipped.push({ vendorId, reason });
      // Register a NotImplementedAdapter so routing doesn't hard-fail
      HalService.registerAdapter(vendorId, createNotImplementedAdapter({}, vendorId));
      continue;
    }

    // ── Step 2a: Per-device factory (Pattern B) — register directly ─────────
    // create*AdapterFactory returns (device) => new Adapter(device, ...).
    // HalService detects functions and calls them with the DeviceRecord at
    // lookup time. Skip singleton-level contract check and probe here.
    if (typeof adapter === 'function') {
      HalService.registerAdapter(vendorId, adapter);
      registered.push(vendorId);
      continue;
    }

    // ── Step 2b: Singleton conformance check (Pattern A) ───────────────────
    if (adapter === null || adapter === undefined) {
      const reason = 'factory returned null/undefined';
      console.warn(`[halBootstrap] "${vendorId}" skipped — ${reason}`);
      skipped.push({ vendorId, reason });
      HalService.registerAdapter(vendorId, createNotImplementedAdapter({}, vendorId));
      continue;
    }

    try {
      assertAdapterContract(adapter, vendorId);
    } catch (err) {
      const reason = `contract violation: ${err?.message ?? String(err)}`;
      console.warn(`[halBootstrap] "${vendorId}" skipped — ${reason}`);
      skipped.push({ vendorId, reason });
      HalService.registerAdapter(vendorId, createNotImplementedAdapter({}, vendorId));
      continue;
    }

    // ── Step 3: Probe (non-blocking; failure doesn't skip registration) ─────
    try {
      const probe = await adapter.probe();
      probeResults.push({ vendorId, ...probe });
      if (!probe.ok) {
        console.warn(`[halBootstrap] "${vendorId}" probe failed: ${probe.error ?? 'unknown'} (registered anyway)`);
      }
    } catch (err) {
      probeResults.push({ vendorId, ok: false, latencyMs: 0, error: err?.message ?? String(err) });
      console.warn(`[halBootstrap] "${vendorId}" probe threw: ${err?.message ?? err} (registered anyway)`);
    }

    // ── Step 4: Register ────────────────────────────────────────────────────
    HalService.registerAdapter(vendorId, adapter);
    registered.push(vendorId);
  }

  return { registered, skipped, probeResults };
}
